
import {formatNumber} from "./reportFormat.js";

import {t} from "../i18n/index.js";
// Canvas-based per-profile chart renderer for the Tværprofiler report (task
// sections 10-12). Replaces the earlier hand-rolled SVG chart builder: the
// SAME drawing function is now used for both the on-screen report preview
// AND the PDF export (CrossSectionPdfBuilder.js calls this directly, rather
// than re-deriving the chart geometry a second time) - so the two can never
// visually drift apart, and jsPDF's addImage() accepts an HTMLCanvasElement
// directly, with no lossy string round-trip through an <img>.
//
// Points are drawn as a scatter (never connected by a line) - point-cloud
// cross-section data is not guaranteed to be a single-valued function of
// lateral position (overhangs, vertical faces, multiple returns), so a line
// could imply a continuous surface that isn't there. Each point keeps its
// own real point-cloud RGB colour (hard requirement, task section 10) -
// falls back to a neutral dark tone only if colour data genuinely isn't
// available.
//
// GEOMETRIC 1:1 CORRECTNESS (task section 12): `exaggeration` is computed so
// that, when it equals 1, the X and Y axes use EXACTLY the same
// pixels-per-metre scale (see pixelsPerMeterY's own derivation below) - not
// just matching numeric axis units. This physical equality only survives
// into the final PDF as long as the whole canvas is embedded at a single
// uniform scale (same mm-per-pixel for width and height, never stretched
// independently to fit a differently-shaped box) - CrossSectionPdfBuilder.js's
// own placement code is responsible for preserving that invariant; this
// module only guarantees the pixel-space geometry is correct to begin with.
const EXAGGERATION_LADDER = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];

// Rounds a raw vertical-exaggeration factor to the nearest "clean"
// cartographic value - closest by ratio (log-space), so e.g. a raw factor
// of 7.4 rounds to 5, not 10 or 1, and 14 rounds to 10.
export function niceExaggeration(raw){
	if(!isFinite(raw) || raw <= 1) return 1;
	let best = EXAGGERATION_LADDER[0], bestDist = Infinity;
	for(const v of EXAGGERATION_LADDER){
		const dist = Math.abs(Math.log(v / raw));
		if(dist < bestDist){ bestDist = dist; best = v; }
	}
	return best;
}

// Fixed *logical* pixel size (before the retina-style `scale` multiplier in
// renderProfileChartCanvas() below) - shared by every chart, so the
// on-screen grid and the PDF grid are laid out identically and every card
// has the exact same aspect ratio.
export const CHART_WIDTH = 380;
export const CHART_HEIGHT = 260;

// Draws one station's chart onto a freshly created <canvas>, rendered at
// `scale`x the logical CHART_WIDTH/CHART_HEIGHT resolution (a standard
// retina-canvas technique: all drawing math below stays in logical units,
// the context is scaled once up front, and the canvas's actual pixel
// dimensions come out sharper without complicating any of the geometry).
// Returns the canvas plus the exaggeration actually used, so a caller that
// wants to show/verify it elsewhere doesn't have to re-derive it.
export function renderProfileChartCanvas(station, profileLength, scale = 2){
	const W = CHART_WIDTH, H = CHART_HEIGHT;
	const canvas = document.createElement("canvas");
	canvas.width = W * scale;
	canvas.height = H * scale;
	const ctx = canvas.getContext("2d");
	ctx.scale(scale, scale);

	const marginLeft = 52, marginRight = 14, marginTop = 30, marginBottom = 34;
	const plotX0 = marginLeft, plotY0 = marginTop;
	const plotW = W - marginLeft - marginRight;
	const plotH = H - marginTop - marginBottom;

	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, W, H);

	ctx.fillStyle = "#111111";
	ctx.font = "bold 14px Arial, sans-serif";
	ctx.textAlign = "left";
	ctx.textBaseline = "alphabetic";
	ctx.fillText(station.name, 8, 17);

	ctx.fillStyle = "#333333";
	ctx.font = "9.5px Arial, sans-serif";
	ctx.fillText(t("Kote (m)"), 10, plotY0 - 8);

	const xScale = (lateral) => plotX0 + ((lateral + profileLength / 2) / profileLength) * plotW;

	ctx.strokeStyle = "#999999";
	ctx.lineWidth = 1;
	ctx.strokeRect(plotX0 + 0.5, plotY0 + 0.5, plotW, plotH);

	ctx.strokeStyle = "#eeeeee";
	ctx.fillStyle = "#333333";
	ctx.font = "9px Arial, sans-serif";
	ctx.textAlign = "center";
	for(const f of [-0.5, -0.25, 0, 0.25, 0.5]){
		const lateral = f * profileLength;
		const x = xScale(lateral);
		ctx.beginPath();
		ctx.moveTo(x, plotY0);
		ctx.lineTo(x, plotY0 + plotH);
		ctx.stroke();
		ctx.fillText(lateral.toFixed(1), x, plotY0 + plotH + 14);
	}

	// task section 11: simplified per-chart caption - just the axis name.
	// The shared "positiv retning er mod venstre..." explanation now lives
	// ONCE elsewhere in the report (CrossSectionReportPanel.js/
	// CrossSectionPdfBuilder.js), not repeated on every single chart.
	ctx.textAlign = "center";
	ctx.fillStyle = "#333333";
	ctx.font = "9.5px Arial, sans-serif";
	ctx.fillText(t("Tværafstand (m)"), W / 2, H - 3);

	const points = station.profilePoints;
	const overlays = station.overlays || [];
	const hasOverlayLines = overlays.some(o => o.lines && o.lines.length > 0);
	let exaggeration = 1, isTrue1to1 = true;

	if(points.length === 0 && !hasOverlayLines){
		ctx.fillStyle = "#999999";
		ctx.font = "12px Arial, sans-serif";
		ctx.textAlign = "center";
		ctx.fillText(t("Ingen punkter fundet"), W / 2, plotY0 + plotH / 2);
	}else{
		let minElev = Infinity, maxElev = -Infinity;
		for(const p of points){
			if(p.elevation < minElev) minElev = p.elevation;
			if(p.elevation > maxElev) maxElev = p.elevation;
		}
		// IFC-snit og terraen (hvis de er taendt) skal ogsaa kunne ses i kote-intervallet
		for(const overlay of overlays){
			for(let i = 0; i < overlay.lines.length; i += 2){
				const z = overlay.lines[i + 1];
				if(z < minElev) minElev = z;
				if(z > maxElev) maxElev = z;
			}
		}
		// a flat/near-flat profile still gets a visible, non-degenerate plot
		const elevRange = Math.max(maxElev - minElev, 0.02);
		const midElev = (minElev + maxElev) / 2;

		const metersPerPixelX = profileLength / plotW;
		const naturalPx = elevRange / metersPerPixelX; // pixel height elevRange would need at true 1:1
		exaggeration = naturalPx < plotH * 0.4
			? niceExaggeration((plotH * 0.8 * metersPerPixelX) / elevRange)
			: 1;
		isTrue1to1 = exaggeration === 1;

		const pixelsPerMeterY = (1 / metersPerPixelX) * exaggeration;
		const yScale = (elevation) => plotY0 + plotH / 2 - (elevation - midElev) * pixelsPerMeterY;

		ctx.strokeStyle = "#e5e5e5";
		ctx.fillStyle = "#333333";
		ctx.font = "9px Arial, sans-serif";
		ctx.textAlign = "right";
		ctx.textBaseline = "middle";
		for(const e of [minElev, midElev, maxElev]){
			const y = yScale(e);
			ctx.beginPath();
			ctx.moveTo(plotX0, y);
			ctx.lineTo(plotX0 + plotW, y);
			ctx.stroke();
			ctx.fillText(e.toFixed(2), plotX0 - 6, y);
		}
		ctx.textBaseline = "alphabetic";

		for(const p of points){
			const x = xScale(p.lateral), y = yScale(p.elevation);
			ctx.beginPath();
			ctx.arc(x, y, 1.6, 0, Math.PI * 2);
			ctx.fillStyle = p.color ? `rgb(${p.color[0]},${p.color[1]},${p.color[2]})` : "#2f4f4f";
			ctx.fill();
		}

		// IFC-snit og terraenlinje, klippet til diagrammets plot-omraade
		if(hasOverlayLines){
			ctx.save();
			ctx.beginPath();
			ctx.rect(plotX0, plotY0, plotW, plotH);
			ctx.clip();
			ctx.lineWidth = 1.4;
			for(const overlay of overlays){
				if(overlay.lineColors){
					// en farve pr. segment (GIS: forsyningsart) - segmenter med samme farve tegnes samlet
					let runColor = null;
					ctx.beginPath();
					for(let i = 0, k = 0; i < overlay.lines.length; i += 4, k += 3){
						const color = `rgb(${Math.round(overlay.lineColors[k] * 255)},${Math.round(overlay.lineColors[k + 1] * 255)},${Math.round(overlay.lineColors[k + 2] * 255)})`;
						if(color !== runColor){
							ctx.stroke();
							ctx.beginPath();
							ctx.strokeStyle = color;
							runColor = color;
						}
						ctx.moveTo(xScale(overlay.lines[i]), yScale(overlay.lines[i + 1]));
						ctx.lineTo(xScale(overlay.lines[i + 2]), yScale(overlay.lines[i + 3]));
					}
					ctx.stroke();
					continue;
				}
				ctx.strokeStyle = overlay.color;
				ctx.beginPath();
				for(let i = 0; i < overlay.lines.length; i += 4){
					ctx.moveTo(xScale(overlay.lines[i]), yScale(overlay.lines[i + 1]));
					ctx.lineTo(xScale(overlay.lines[i + 2]), yScale(overlay.lines[i + 3]));
				}
				ctx.stroke();
			}
			ctx.restore();

			// forklaring inde i diagrammet (oeverst til venstre, paa en let hvid baggrund)
			ctx.font = "9px Arial, sans-serif";
			ctx.textAlign = "left";
			ctx.textBaseline = "alphabetic";
			const legendItems = overlays.filter(o => o.lines && o.lines.length > 0);
			let legendWidth = 8;
			for(const overlay of legendItems){
				legendWidth += 14 + ctx.measureText(overlay.label).width + 10;
			}
			ctx.fillStyle = "rgba(255,255,255,0.85)";
			ctx.fillRect(plotX0 + 2, plotY0 + 2, legendWidth, 14);
			let legendX = plotX0 + 8;
			for(const overlay of legendItems){
				ctx.strokeStyle = overlay.color;
				ctx.lineWidth = 1.6;
				ctx.beginPath();
				ctx.moveTo(legendX, plotY0 + 9);
				ctx.lineTo(legendX + 10, plotY0 + 9);
				ctx.stroke();
				ctx.fillStyle = "#333333";
				ctx.fillText(overlay.label, legendX + 14, plotY0 + 12);
				legendX += 14 + ctx.measureText(overlay.label).width + 10;
			}
			ctx.lineWidth = 1;
		}

		ctx.fillStyle = "#555555";
		ctx.font = "9px Arial, sans-serif";
		ctx.textAlign = "right";
		ctx.textBaseline = "alphabetic";
		const exaggerationLabel = isTrue1to1 ? t("1:1 (ingen højdeforstørrelse)") : t("Højdeforstørrelse: {0}×", formatNumber(exaggeration, 0));
		ctx.fillText(exaggerationLabel, W - marginRight, 17);
	}

	return {canvas, exaggeration, isTrue1to1};
}
